import React, { useEffect, useState } from 'react'
import { Box, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import DownloadPincodesButton from './components/DownloadPincodesButton'
import { intelApi } from './api'
import { intelColors, formatNumber, formatPct } from './components/intelTheme'

// Forward / Reverse chip — mirrors Delivery Coverage. Filters which sections show (data for both
// directions already arrives in one response, so this is a pure client-side view toggle).
const DIRECTIONS = [
  { val: 'ALL', label: 'All' },
  { val: 'FORWARD', label: 'Forward' },
  { val: 'REVERSE', label: 'Reverse' },
]

const payModeBadge = (m) => {
  const map = { BOTH: { color: 'green', label: 'Both' }, COD: { color: 'orange', label: 'COD' }, PREPAID: { color: 'blue', label: 'Prepaid' } }
  const cfg = map[m] || { color: 'grey', label: '—' }
  return <Badge color={cfg.color}>{cfg.label}</Badge>
}

const stateBadge = (s) => {
  const map = {
    ACTIVE: { color: 'green', label: '✓ Active' },
    PARTIAL: { color: 'orange', label: 'Partial' },
    OVERLAY_ONLY: { color: 'grey', label: 'Overlay only' },
    PENDING_SYNC: { color: 'grey', label: 'Pending sync' },
  }
  const cfg = map[s] || { color: 'grey', label: s || '—' }
  return <Badge color={cfg.color}>{cfg.label}</Badge>
}

// Dropdown filter. options accept either plain strings or { value, label } objects.
const FilterField = ({ label, value, onChange, options = [], allLabel = 'All', disabled, helperText, width = 180 }) => (
  <Box sx={{ position: 'relative' }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>{label}</Typography>
    <TextField
      select
      size='small'
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      SelectProps={{ displayEmpty: true }}
      helperText={helperText}
      sx={{ width, '& .MuiSelect-select': { fontSize: 12, padding: '7px 10px' }, '& .MuiFormHelperText-root': { fontSize: 10, m: 0, position: 'absolute', top: '100%', left: 0, whiteSpace: 'nowrap' } }}
    >
      <MenuItem value='' sx={{ fontSize: 12 }}>{allLabel}</MenuItem>
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value
        const l = typeof o === 'string' ? o : o.label || o.value
        return (
          <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>{l}</MenuItem>
        )
      })}
    </TextField>
  </Box>
)

export default function CoveragePickup() {
  const [parentCp, setParentCp] = useState('')
  const [serviceType, setServiceType] = useState('')
  const [accountCode, setAccountCode] = useState('')
  const [direction, setDirection] = useState('ALL')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  // Dropdown option lists. parent + service type load once; account codes cascade off parent.
  const [parentOptions, setParentOptions] = useState([])
  const [serviceTypeOptions, setServiceTypeOptions] = useState([])
  const [accountCodeOptions, setAccountCodeOptions] = useState([])

  useEffect(() => {
    let cancel = false
    intelApi
      .formOptions()
      .then((res) => {
        if (cancel) return
        const opts = res?.data?.responseObj || res?.data?.data || res?.data || {}
        setParentOptions(opts.parentCps || [])
        setServiceTypeOptions(opts.serviceTypes || [])
      })
      .catch(() => {})
    return () => {
      cancel = true
    }
  }, [])

  useEffect(() => {
    if (!parentCp) {
      setAccountCodeOptions([])
      return
    }
    let cancel = false
    intelApi
      .accountCodes({ parent_cp: parentCp })
      .then((res) => {
        if (cancel) return
        const body = res?.data?.responseObj || res?.data?.data || res?.data || {}
        setAccountCodeOptions((body.accountCodes || []).map((c) => c.accountCode || c).filter(Boolean))
      })
      .catch(() => !cancel && setAccountCodeOptions([]))
    return () => {
      cancel = true
    }
  }, [parentCp])

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .coveragePickup({ parentCp, serviceType, accountCode })
      .then((res) => !cancel && setData(res?.data?.responseObj || res?.data?.data || res?.data))
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [parentCp, serviceType, accountCode])

  const fwd = data?.forward || {}
  const rev = data?.reverse || {}

  const fwdCols = [
    { key: 'st', header: 'Service Type', info: 'service_type_master where direction = FORWARD (every forward ST listed as the row spine).', render: (r) => <strong>{r.serviceTypeName}</strong> },
    { key: 'code', header: 'ST Code', info: 'service_type_master.code.', render: (r) => <code style={{ background: '#f1f3f5', padding: '1px 5px', borderRadius: 3 }}>{r.stCode}</code> },
    { key: 'mode', header: 'Mode', info: 'service_type_master.mode (SURFACE / AIR / HYPERLOCAL).', render: (r) => <Badge color={r.mode === 'AIR' ? 'purple' : r.mode === 'HYPERLOCAL' ? 'grey' : 'blue'}>{r.mode}</Badge> },
    { key: 'pins', header: 'Pickup Pincodes', info: 'Distinct union of pickupPincodesPrepaid across the active FORWARD codes mapping this ST (current filters).', render: (r) => formatNumber(r.pickupPincodes) },
    { key: 'cods', header: 'Courier Codes', info: 'Active codes whose service_type_primary = this ST.', render: (r) => (r.courierCodes || []).join(', ') },
    { key: 'pay', header: 'Payment Mode', info: 'Payment modes the mapped active codes collectively support, from isCod + isPrepaid: Both / COD / Prepaid / — (none). (Replaces the old "COD at Pickup" — there is no pickup-time COD data source; COD is collected on delivery.)', render: (r) => payModeBadge(r.paymentMode) },
    { key: 'cut', header: 'Pickup Cutoff', info: 'Earliest (min) courier_partners.pickup_cutoff_time across the matched codes.', render: (r) => r.pickupCutoffTime || '—' },
    { key: 'srv', header: 'Serviceable', info: 'OVERLAY_ONLY if no code maps this ST; else ACTIVE.', render: (r) => stateBadge(r.serviceable) },
    {
      key: 'dl', header: 'Pincodes', info: 'Download the actual pickup pincode list (merged across every account code mapping this ST).',
      render: (r) => (
        <DownloadPincodesButton
          accountCodes={r.courierCodes}
          filenameBase={`forward_pickup_${r.stCode}`}
          options={[{ key: 'pickup', label: 'Pickup Pincodes', count: r.pickupPincodes, pincodeTypes: ['PICKUP_PREPAID'] }]}
        />
      ),
    },
  ]

  const revCols = [
    { key: 'st', header: 'Service Type', info: 'service_type_master where direction = RVP (every reverse ST listed as the row spine).', render: (r) => <strong>{r.serviceTypeName}</strong> },
    { key: 'code', header: 'ST Code', info: 'service_type_master.code.', render: (r) => <code style={{ background: '#f1f3f5', padding: '1px 5px', borderRadius: 3 }}>{r.stCode}</code> },
    { key: 'qc', header: 'QC at Pickup', info: 'service_type_master.requires_qc — whether this ST requires quality check at pickup.', render: (r) => (r.qcAtPickup ? <Badge color='blue'>Yes</Badge> : <Badge color='grey'>No</Badge>) },
    { key: 'pins', header: 'Pickup Pincodes', info: 'Distinct union of pickupPincodesPrepaid across the active RVP codes mapping this ST (current filters).', render: (r) => formatNumber(r.pickupPincodes) },
    { key: 'cods', header: 'Courier Codes', info: 'Active RVP codes whose service_type_primary = this ST.', render: (r) => (r.courierCodes || []).join(', ') },
    { key: 'pay', header: 'Payment Mode', info: 'Payment modes the mapped active codes collectively support, from isCod + isPrepaid: Both / COD / Prepaid / — (none). (Replaces the old "COD Refund" — there is no reverse cash-refund data source; this is the codes\' payment capability.)', render: (r) => payModeBadge(r.paymentMode) },
    { key: 'sla', header: 'Avg Pickup SLA', info: 'service_type_master SLA description for the ST.', render: (r) => r.avgPickupSla || '—' },
    { key: 'srv', header: 'Serviceable', info: 'OVERLAY_ONLY if no code maps this ST; else ACTIVE.', render: (r) => stateBadge(r.serviceable) },
    {
      key: 'dl', header: 'Pincodes', info: 'Download the actual pickup pincode list (merged across every account code mapping this ST).',
      render: (r) => (
        <DownloadPincodesButton
          accountCodes={r.courierCodes}
          filenameBase={`reverse_pickup_${r.stCode}`}
          options={[{ key: 'pickup', label: 'Pickup Pincodes', count: r.pickupPincodes, pincodeTypes: ['PICKUP_PREPAID'] }]}
        />
      ),
    },
  ]

  return (
    <IntelLayout title='Pickup Coverage'>
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${intelColors.border}`,
          borderRadius: '8px',
          padding: '14px 18px',
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <FilterField
          label='Parent Courier'
          value={parentCp}
          onChange={(v) => { setParentCp(v); setAccountCode('') }}
          options={parentOptions}
          allLabel='All Parents'
        />
        <FilterField
          label='Service Type'
          value={serviceType}
          onChange={setServiceType}
          options={serviceTypeOptions}
          allLabel='All Service Types'
          width={200}
        />
        <FilterField
          label='Courier Code'
          value={accountCode}
          onChange={setAccountCode}
          options={accountCodeOptions}
          disabled={!parentCp}
          helperText={!parentCp ? 'Select a parent first' : ''}
          allLabel='All Codes'
          width={220}
        />
      </Box>

      <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          size='small'
          value={direction}
          onChange={(_, v) => v && setDirection(v)}
          sx={{
            '& .MuiToggleButton-root': { padding: '4px 12px', fontSize: 11, fontWeight: 600, textTransform: 'none', borderRadius: '12px !important', mx: '2px', border: '1px solid #d0d7de' },
            '& .Mui-selected': { background: '#e3f2fd !important', color: `${intelColors.primary} !important`, borderColor: `${intelColors.primary} !important` },
          }}
        >
          {DIRECTIONS.map((d) => (
            <ToggleButton key={d.val} value={d.val}>{d.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      {direction !== 'REVERSE' && (
      <>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text, mb: 1.5 }}>Forward Pickup</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='blue' label='Forward Pickup Enabled' value={formatNumber(fwd.kpis?.primary?.value)} sub={fwd.kpis?.primary && `${formatPct(fwd.kpis.primary.pctOfUniverse)} of universe`} />
        <KpiCard loading={loading} color='green' label='Same-Day Pickup' value={formatNumber(fwd.kpis?.secondary?.value)} sub={fwd.kpis?.secondary && `${formatPct(fwd.kpis.secondary.pctOfUniverse)} of universe`} />
        <KpiCard loading={loading} color='orange' label='COD Pickup' value={formatNumber(fwd.kpis?.tertiary?.value)} sub={fwd.kpis?.tertiary && `${formatPct(fwd.kpis.tertiary.pctOfUniverse)} of universe`} />
        <KpiCard loading={loading} color='red' label='No Forward Pickup' value={formatNumber(fwd.kpis?.gap?.value)} sub='Delivery-only pincodes' />
      </Box>
      <IntelTable
        title='Forward Pickup by Service Type'
        loading={loading}
        columns={fwdCols}
        rows={fwd.forwardRows || []}
        rowKey={(r, i) => r.stCode || i}
        emptyMsg='No forward pickup rows'
      />
      </>
      )}

      {direction !== 'FORWARD' && (
      <>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text, mb: 1.5, mt: 3 }}>Reverse Pickup</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='orange' label='RVP Pickup Enabled' value={formatNumber(rev.kpis?.primary?.value)} sub={rev.kpis?.primary && `${formatPct(rev.kpis.primary.pctOfUniverse)} of universe`} />
        <KpiCard loading={loading} color='green' label='QC-Capable Pickup' value={formatNumber(rev.kpis?.secondary?.value)} sub={rev.kpis?.secondary && `${formatPct(rev.kpis.secondary.pctOfUniverse)} of universe`} />
        <KpiCard loading={loading} color='blue' label='High-Value Pickup' value={formatNumber(rev.kpis?.tertiary?.value)} sub={rev.kpis?.tertiary && `${formatPct(rev.kpis.tertiary.pctOfUniverse)} of universe`} />
        <KpiCard loading={loading} color='red' label='No RVP Coverage' value={formatNumber(rev.kpis?.gap?.value)} sub='Delivery-only pincodes' />
      </Box>
      <IntelTable
        title='Reverse Pickup by Service Type'
        loading={loading}
        columns={revCols}
        rows={rev.reverseRows || []}
        rowKey={(r, i) => r.stCode || i}
        emptyMsg='No reverse pickup rows'
      />
      </>
      )}
    </IntelLayout>
  )
}
